import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Plus, Pencil, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, Field, NativeSelect, Logo, useSettings, Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CONFIG = {
  sectors: { title: "Settori motoristici", empty: { code: "", name: "", region: "", active: true }, fields: [["code", "Codice"], ["name", "Nome"], ["region", "Area territoriale"]] },
  disciplines: { title: "Discipline sportive", empty: { code: "", name: "", active: true }, fields: [["code", "Codice"], ["name", "Nome"]] },
  categories: { title: "Categorie di abilitazione", empty: { code: "", name: "", discipline_id: "", description: "", covers: [], active: true }, fields: [["code", "Codice"], ["name", "Nome"], ["description", "Descrizione"]] },
};

function ItemDialog({ kind, item, open, onOpenChange, onSaved, disciplines, categories }) {
  const cfg = CONFIG[kind];
  const [f, setF] = useState(cfg.empty);
  useEffect(() => { if (open) setF(item ? { ...cfg.empty, ...item } : cfg.empty); }, [open, item, cfg.empty]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(Object.keys(cfg.empty).map((k) => [k, f[k]]));
      item ? await api.put(`/${kind}/${item.id}`, payload) : await api.post(`/${kind}`, payload);
      toast.success("Salvato");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const toggleCover = (id) => setF({ ...f, covers: f.covers.includes(id) ? f.covers.filter((c) => c !== id) : [...f.covers, id] });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{item ? "Modifica" : "Nuovo elemento"} · {cfg.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {cfg.fields.map(([k, label]) => (
            <Field key={k} label={label}><Input value={f[k] || ""} required={k !== "description" && k !== "region"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${kind}-${k}-input`} /></Field>
          ))}
          {kind === "categories" && (
            <>
              <Field label="Disciplina">
                <NativeSelect value={f.discipline_id} onChange={(v) => setF({ ...f, discipline_id: v, covers: [] })} required testid="categories-discipline-select">
                  <option value="">Seleziona…</option>
                  {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </NativeSelect>
              </Field>
              {f.discipline_id && (
                <div>
                  <div className="text-sm font-medium text-slate-700">Corrispondenze: questa abilitazione consente anche</div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {categories.filter((c) => c.discipline_id === f.discipline_id && c.id !== item?.id).map((c) => (
                      <label key={c.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm">
                        <input type="checkbox" checked={f.covers.includes(c.id)} onChange={() => toggleCover(c.id)} data-testid={`categories-cover-${c.code}`} />{c.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid={`${kind}-active-switch`} />Attivo</label>
          <DialogFooter><Button type="submit" className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid={`${kind}-submit-btn`}>Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CatalogTab({ kind, items, disciplines, categories, onSaved }) {
  const [dlg, setDlg] = useState({ open: false, item: null });
  const name = (id) => categories.find((c) => c.id === id)?.name;
  return (
    <div className="card-surface p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="font-display text-base font-semibold">{CONFIG[kind].title}</div>
        <Button size="sm" onClick={() => setDlg({ open: true, item: null })} data-testid={`${kind}-new-btn`}><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-3 py-3" data-testid={`${kind}-row-${it.code}`}>
            <div>
              <div className="font-medium text-slate-900">{it.name} <span className="ml-1 font-mono text-xs text-slate-400">{it.code}</span></div>
              <div className="text-xs text-slate-500">
                {kind === "categories" ? `${disciplines.find((d) => d.id === it.discipline_id)?.name || ""}${it.covers?.length ? ` · consente anche: ${it.covers.map(name).join(", ")}` : ""}` : it.region || ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!it.active && <Badge cls="bg-slate-100 text-slate-600 border-slate-200">Disattivo</Badge>}
              <Button size="sm" variant="ghost" onClick={() => setDlg({ open: true, item: it })} data-testid={`${kind}-edit-${it.code}`}><Pencil className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <ItemDialog kind={kind} item={dlg.item} open={dlg.open} onOpenChange={(v) => setDlg({ ...dlg, open: v })} onSaved={onSaved} disciplines={disciplines} categories={categories} />
    </div>
  );
}

function RemindersTab() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/reminders").then((r) => setD(r.data)).catch(() => setD({ logs: [], runs: [], days: 30 }));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/reminders/run");
      toast.success("Invio promemoria avviato");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const cls = { inviata: "bg-emerald-50 text-emerald-800 border-emerald-200", saltata: "bg-slate-100 text-slate-600 border-slate-200", errore: "bg-red-50 text-red-800 border-red-200" };
  return (
    <div className="space-y-4">
      <div className="card-surface flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-display text-base font-semibold">Promemoria scadenze automatici</div>
          <div className="text-sm text-slate-600">Ogni giorno alle 8:00 il sistema invia un'email all'atleta e un riepilogo ai responsabili del settore per le licenze che scadono entro {d?.days || 30} giorni. Ogni licenza riceve un solo promemoria per scadenza.</div>
        </div>
        <Button onClick={run} disabled={busy} className="shrink-0 bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="reminders-run-btn"><Send className="mr-2 h-4 w-4" />Esegui ora</Button>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm" data-testid="reminders-table">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Data e ora</th><th className="px-4 py-3">Destinatario</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Esito</th><th className="px-4 py-3">Dettagli</th></tr>
          </thead>
          <tbody>
            {(d?.logs || []).length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Nessun promemoria inviato finora.</td></tr>}
            {(d?.logs || []).map((l) => (
              <tr key={l.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 whitespace-nowrap">{fmtDateTime(l.at)}</td>
                <td className="px-4 py-2.5">{l.to}</td>
                <td className="px-4 py-2.5 capitalize">{l.kind}</td>
                <td className="px-4 py-2.5"><Badge cls={cls[l.status]}>{l.status}</Badge></td>
                <td className="px-4 py-2.5 text-slate-600">{l.detail || `${l.license_ids.length} licenze`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Settings() {
  const settings = useSettings();
  const [data, setData] = useState({ sectors: [], disciplines: [], categories: [] });
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([api.get("/sectors"), api.get("/disciplines"), api.get("/categories")])
    .then(([s, d, c]) => setData({ sectors: s.data, disciplines: d.data, categories: c.data }));
  useEffect(() => { load(); api.get("/audit-logs").then((r) => setLogs(r.data)); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { timeout: 60000 });
      toast.success("Logo aggiornato");
      window.dispatchEvent(new Event("settings:changed"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Configurazione nazionale" title="Impostazioni" />
      <Tabs defaultValue="logo">
        <TabsList className="flex-wrap">
          <TabsTrigger value="logo" data-testid="settings-tab-logo">Logo</TabsTrigger>
          <TabsTrigger value="sectors" data-testid="settings-tab-sectors">Settori</TabsTrigger>
          <TabsTrigger value="disciplines" data-testid="settings-tab-disciplines">Discipline</TabsTrigger>
          <TabsTrigger value="categories" data-testid="settings-tab-categories">Categorie</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-tab-audit">Registro operazioni</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="settings-tab-reminders">Promemoria email</TabsTrigger>
        </TabsList>
        <TabsContent value="logo" className="mt-4">
          <div className="card-surface flex flex-col items-start gap-5 p-6 sm:flex-row sm:items-center">
            <Logo settings={settings} className="h-24 w-24" />
            <div>
              <div className="font-display text-base font-semibold">Logo MSP Italia</div>
              <div className="text-sm text-slate-600">Usato nel menu, nella tessera digitale e nel PDF della licenza. PNG/JPG/WEBP, max 3 MB.</div>
              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#0F2042] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1E3A8A]">
                <Upload className="h-4 w-4" />{busy ? "Caricamento…" : "Carica logo"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="settings-logo-input" />
              </label>
            </div>
          </div>
        </TabsContent>
        {["sectors", "disciplines", "categories"].map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <CatalogTab kind={k} items={data[k]} disciplines={data.disciplines} categories={data.categories} onSaved={load} />
          </TabsContent>
        ))}
        <TabsContent value="reminders" className="mt-4"><RemindersTab /></TabsContent>
        <TabsContent value="audit" className="mt-4">
          <div className="card-surface overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm" data-testid="audit-table">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Data e ora</th><th className="px-4 py-3">Operatore</th><th className="px-4 py-3">Operazione</th><th className="px-4 py-3">Dettagli</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDateTime(l.at)}</td>
                    <td className="px-4 py-2.5">{l.actor_name}</td>
                    <td className="px-4 py-2.5 font-medium">{l.action}</td>
                    <td className="px-4 py-2.5 text-slate-600">{l.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
